import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './Sidebar.css';

const Sidebar = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userName, setUserName] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (user) {
      fetchUserName();
    }
  }, [user]);

  const fetchUserName = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('name')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      
      if (data && data.name) {
        setUserName(data.name);
      } else {
        // Fallback to email username if no name in database
        setUserName(user.email?.split('@')[0] || '');
      }
    } catch (error) {
      console.error('Error fetching user name:', error);
      // Fallback to email username
      setUserName(user.email?.split('@')[0] || '');
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  return (
    <>
      {/* Mobile Header */}
      {user && (
        <div className="mobile-header">
          <h1 className="mobile-header-logo">Mockbox</h1>
          <button 
            className="mobile-menu-button"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              {isMobileMenuOpen ? (
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              ) : (
                <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              )}
            </svg>
          </button>
        </div>
      )}

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-overlay" onClick={closeMobileMenu}></div>
      )}

      <div className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        {user && (
          <>
            {/* Logo Section */}
            <div className="sidebar-header">
              <h1 className="sidebar-logo">Mockbox</h1>
            </div>

          {/* Main Navigation */}
          <nav className="sidebar-nav">
            <div className="nav-section">
              <Link 
                to="/home" 
                className={`nav-item ${isActive('/home') ? 'active' : ''}`}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Home</span>
              </Link>

              <Link 
                to="/resumes" 
                className={`nav-item ${isActive('/resumes') ? 'active' : ''}`}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Resumes</span>
              </Link>

              <Link 
                to="/jobs" 
                className={`nav-item ${isActive('/jobs') ? 'active' : ''}`}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H20C21.1046 21 22 20.1046 22 19V9C22 7.89543 21.1046 7 20 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 21V5C16 4.46957 15.7893 3.96086 15.4142 3.58579C15.0391 3.21071 14.5304 3 14 3H10C9.46957 3 8.96086 3.21071 8.58579 3.58579C8.21071 3.96086 8 4.46957 8 5V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Jobs</span>
              </Link>

            </div>
          </nav>

          {/* User Profile Section - Bottom */}
          <div className="sidebar-footer">
            <div className="user-profile">
              <div className="user-avatar-small">
                {userName ? userName.charAt(0).toUpperCase() : user.email?.charAt(0).toUpperCase()}
              </div>
              <div className="user-info-header">
                <h3 className="user-name">
                  {userName || user.email?.split('@')[0]}
                </h3>
              </div>
            </div>
            <Link 
              to="/settings" 
              className="settings-icon-button"
              title="Settings"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 12C11.1046 12 12 11.1046 12 10C12 8.89543 11.1046 8 10 8C8.89543 8 8 8.89543 8 10C8 11.1046 8.89543 12 10 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 10C16 10.3128 15.9752 10.6194 15.9279 10.9176L17.6454 12.1856C17.7947 12.2975 17.8357 12.5056 17.7406 12.6693L16.1406 15.3307C16.0454 15.4944 15.8454 15.5599 15.6763 15.4894L13.6279 14.6521C13.2228 14.9655 12.7773 15.2235 12.3011 15.4182L11.9764 17.5788C11.9486 17.7607 11.7864 17.9 11.5918 17.9H8.40816C8.21358 17.9 8.05142 17.7607 8.02358 17.5788L7.69888 15.4182C7.22268 15.2235 6.77723 14.9655 6.37207 14.6521L4.32373 15.4894C4.15456 15.5599 3.95456 15.4944 3.85936 15.3307L2.25936 12.6693C2.16427 12.5056 2.20525 12.2975 2.3546 12.1856L4.07207 10.9176C4.0248 10.6194 4 10.3128 4 10C4 9.68723 4.0248 9.38062 4.07207 9.08238L2.3546 7.81443C2.20525 7.70253 2.16427 7.49444 2.25936 7.33069L3.85936 4.66931C3.95456 4.50556 4.15456 4.44007 4.32373 4.51056L6.37207 5.34793C6.77723 5.03448 7.22268 4.77654 7.69888 4.58176L8.02358 2.42119C8.05142 2.23931 8.21358 2.1 8.40816 2.1H11.5918C11.7864 2.1 11.9486 2.23931 11.9764 2.42119L12.3011 4.58176C12.7773 4.77654 13.2228 5.03448 13.6279 5.34793L15.6763 4.51056C15.8454 4.44007 16.0454 4.50556 16.1406 4.66931L17.7406 7.33069C17.8357 7.49444 17.7947 7.70253 17.6454 7.81443L15.9279 9.08238C15.9752 9.38062 16 9.68723 16 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </>
      )}
      </div>
    </>
  );
};

export default Sidebar;
